// ─── Environment ──────────────────────────────────────────────────────────────

export const IS_DEV = process.env.NODE_ENV === "development";

// ─── API ──────────────────────────────────────────────────────────────────────

/** Base URL for all backend API calls. Override via REACT_APP_API_BASE_URL in .env */
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api/v1";

// ─── API routes ───────────────────────────────────────────────────────────────

export const ROUTES = {
  PATIENTS:          "/patients",
  PATIENT:           (id: string) => `/patients/${id}`,
  PATIENT_SEARCH:    (q: string, limit = 8) => `/patients/search?q=${encodeURIComponent(q)}&limit=${limit}`,
  PATIENT_ALLERGIES: (id: string) => `/patients/${id}/allergies`,
  PATIENT_ALLERGY:   (pid: string, aid: string) => `/patients/${pid}/allergies/${aid}`,
  DRUG_CHECK:        (id: string) => `/patients/${id}/allergies/check-drug`,
  PATIENT_NOTES:         (id: string) => `/patients/${id}/notes`,
  PATIENT_NOTE:          (pid: string, entryId: string) => `/patients/${pid}/notes/${entryId}`,
  PATIENT_NOTE_HISTORY:  (pid: string, entryId: string) => `/patients/${pid}/notes/${entryId}/history`,
  VISITS:            "/visits",
  VISIT:             (id: string) => `/visits/${id}`,
  DOCTOR_TEMPLATES:  (kind: string) => `/doctor-templates?kind=${encodeURIComponent(kind)}`,
  DOCTOR_TEMPLATE_CREATE: "/doctor-templates",
  DOCTOR_TEMPLATE:   (id: string) => `/doctor-templates/${id}`,
  VISIT_FINALIZE:    (id: string) => `/visits/${id}/finalize`,
  VISIT_VITALS:      (id: string) => `/visits/${id}/vitals`,
  VISIT_SYNC:               "/visits/sync",
  VISIT_PENDING_COUNT: "/visits/pending-count",
  VISITS_PENDING:           "/visits/pending",
  VISIT_PRESCRIPTIONS:   (id: string) => `/visits/${id}/prescriptions`,
  VISIT_PRESCRIPTION:    (vid: string, pid: string) => `/visits/${vid}/prescriptions/${pid}`,
  VISIT_SOAP_NOTE:       (id: string) => `/visits/${id}/soap-note`,
  VISIT_NOTES:           (id: string) => `/visits/${id}/notes`,
  NOTE:                  (entryId: string) => `/notes/${entryId}`,
  NOTE_VERSIONS:         (entryId: string) => `/notes/${entryId}/versions`,
  // Deliberately /visit-notes, not /notes - PATIENT_NOTES above is PR #204's
  // unrelated patient-level notes feature at the same /patients/{id}/notes path.
  PATIENT_VISIT_NOTES:   (id: string) => `/patients/${id}/visit-notes`,
  PATIENT_VISITS:        (id: string) => `/patients/${id}/visits`,
  PATIENT_VISITS_WITH_RX: (id: string) => `/patients/${id}/visits-with-rx`,
  PATIENT_BILLABLE_VISITS: (id: string) => `/patients/${id}/billable-visits`,
  VITALS_TREND:          (id: string) => `/patients/${id}/vitals/trend`,
  GROWTH_CHART:          (id: string) => `/patients/${id}/growth-chart`,
  GROWTH_CHART_TREND:    (id: string) => `/patients/${id}/growth-chart/trend`,
  GROWTH_CHART_SHARE:    (id: string) => `/patients/${id}/growth-chart/share`,
  AUTH_ROLE:         "/auth/role",
  AUTH_ME:           "/auth/me",
  ONBOARDING_STATUS: "/onboarding/status",
  ONBOARDING_CLINIC: "/onboarding/clinic",
  CLINIC_BY_ID:      (id: string) => `/clinics/${id}`,
  ONBOARDING_DOCS:            "/onboarding/documents",
  ONBOARDING_DOCS_PRESIGNED:  "/onboarding/documents/presigned-urls",
  ONBOARDING_VERIFY_EMAIL:    "/onboarding/verify-email",
  USER_INFO:                  (id: string) => `/users/${id}/info`,
  VISIT_PRESCRIPTION_OBS:           (vid: string, pid: string) => `/visits/${vid}/prescriptions/${pid}/observation`,
  VISIT_PRESCRIPTION_GENERATE_PDF:  (vid: string, pid: string) => `/visits/${vid}/prescriptions/${pid}/generate-pdf`,
  VISIT_PRESCRIPTION_PDF_URL:       (vid: string, pid: string) => `/visits/${vid}/prescriptions/${pid}/pdf-url`,
  PRESCRIPTION_FINALISE: (id: string)=> `/prescriptions/${id}/finalise`,
  PRESCRIPTION_SEND_EMAIL: (id: string)=> `/prescriptions/${id}/send-email`,
  PRESCRIPTION_SEND_WHATSAPP: (id: string)=> `/prescriptions/${id}/send-whatsapp`,
  PRESCRIPTION_DELIVERY_STATUS: (id: string)=> `/prescriptions/${id}/delivery-status`,
  DRUGS:                      "/drugs",
  DRUG:                       (id: string) => `/drugs/${id}`,
  DOCTORS:                "/doctors",
  DOCTOR:                 (id: string) => `/doctors/${id}`,
  DOCTOR_STATS:           "/doctors/stats",
  DOCTOR_INVITE_STATUS:   (id: string) => `/doctors/${id}/invite-status`,
  DOCTOR_RESEND_INVITE:   (id: string) => `/doctors/${id}/resend-invite`,
  SLOTS:               "/slots",
  APPOINTMENTS:        "/appointments",
  APPOINTMENT:         (id: string) => `/appointments/${id}`,
  APPOINTMENT_CONFIRM: (id: string) => `/appointments/${id}/confirm`,
  APPOINTMENT_CANCEL:  (id: string) => `/appointments/${id}/cancel`,
  APPOINTMENT_RESCHEDULE: (id: string) => `/appointments/${id}/reschedule`,
  APPOINTMENT_CHECKIN: (id: string) => `/appointments/${id}/checkin`,
  APPOINTMENT_COMPLETE: (id: string) => `/appointments/${id}/complete`,
  APPOINTMENT_NOSHOW:  (id: string) => `/appointments/${id}/no-show`,
  INVOICE_CREATE:          (visitId: string) => `/visits/${visitId}/invoices`,
  INVOICE:                 (id: string) => `/invoices/${id}`,
  INVOICE_SEND:            (id: string) => `/invoices/${id}/send`,
  INVOICE_RECORD_PAYMENT:  (id: string) => `/invoices/${id}/record-payment`,
  INVOICE_MARK_PAID:       (id: string) => `/invoices/${id}/mark-paid`,
  INVOICE_PDF:             (id: string) => `/invoices/${id}/pdf`,
  INVOICE_SEND_WHATSAPP:   (id: string) => `/invoices/${id}/send-whatsapp`,
  INVOICE_SEND_EMAIL:      (id: string) => `/invoices/${id}/send-email`,
  PATIENT_INVOICES:        (patientId: string) => `/patients/${patientId}/invoices`,

  // ─── Settings ────────────────────────────────────────────────────────────────

  // Profile & Account
  CLINIC_PROFILE:               "/clinic/profile",       // GET + PUT — aggregate user + clinic
  ONBOARDING_PROFILE:           "/onboarding/profile",   // POST — exists but was missing from ROUTES

  // Doctor KYC — per-doctor identity / credential documents (private bucket).
  DOCTOR_KYC:           (doctorId: string) => `/doctors/${doctorId}/kyc`,
  DOCTOR_KYC_PRESIGN:   (doctorId: string) => `/doctors/${doctorId}/kyc/presigned-urls`,
  DOCTOR_KYC_VIEW_URL:  (doctorId: string, kycDocId: string) => `/doctors/${doctorId}/kyc/${kycDocId}/view-url`,

  // Clinic Settings
  CLINIC_SETTINGS:              "/clinic/settings",
  CLINIC_LETTERHEAD:            "/clinic/settings/letterhead",
  CLINIC_STAFF:                 "/clinic/staff",          // TODO: pending NB1 — may stay as DOCTORS
  CLINIC_APPOINTMENT_CONFIG:    "/clinic/appointment-config",
  CLINIC_RESCHEDULE_POLICY:     "/clinic/reschedule-policy",
  CLINIC_HRF_SETTINGS:          "/clinic/hrf-settings",
  CLINIC_VISIT_NOTE_EDIT_POLICY: "/clinic/visit-note-edit-policy",
  CLINIC_NOTE_EDIT_POLICY:      "/clinic/note-edit-policy",
  CLINIC_IMMUNIZATION_SETTINGS: "/clinic/immunization-settings",

  // Access & Personalizations
  CLINIC_PERMISSIONS:           "/clinic/permissions",

  // Finance & Billing
  CLINIC_FINANCE_SETTINGS:      "/clinic/finance-settings",

  // Integrations
  CLINIC_INTEGRATIONS:          "/clinic/integrations",   // non-admin read-only (decided BN2)
  ADMIN_FEATURES:               (clinicId: string) => `/admin/clinics/${clinicId}/features`,
  ADMIN_FEATURE:                (clinicId: string, feature: string) => `/admin/clinics/${clinicId}/features/${feature}`,

  // System & Preferences
  CLINIC_NOTIFICATION_SETTINGS: "/clinic/notification-settings",
  CLINIC_BACKUP_EXPORT:         "/clinic/backup-export",  // Admin-only; audits the client-side PHI backup

  // Data & Privacy — full-clinic export job
  CLINIC_EXPORT:                "/clinic/export",
  CLINIC_EXPORT_JOB:            (jobId: string) => `/clinic/export/${jobId}`,
  CLINIC_EXPORT_JOB_CANCEL:     (jobId: string) => `/clinic/export/${jobId}/cancel`,

  // Consent audit log — called after sign-in to enrich the consent record with device details
  ONBOARDING_CONSENT:           "/onboarding/consent",

  // Patient Documents — upload, view, search, sensitive ACL
  PATIENT_DOCUMENT_CATEGORIES:  (pid: string) => `/patients/${pid}/documents/categories`,
  PATIENT_DOCUMENT_UPLOAD_URLS: (pid: string) => `/patients/${pid}/documents/upload-urls`,
  PATIENT_DOCUMENT_CONFIRM:     (pid: string) => `/patients/${pid}/documents/confirm`,
  PATIENT_DOCUMENTS:            (pid: string) => `/patients/${pid}/documents`,
  PATIENT_DOCUMENT_URL:         (pid: string, did: string) => `/patients/${pid}/documents/${did}/url`,
  PATIENT_DOCUMENT_PREVIEW_URL: (pid: string, did: string) => `/patients/${pid}/documents/${did}/preview-url`,
  PATIENT_DOCUMENT_SENSITIVITY: (pid: string, did: string) => `/patients/${pid}/documents/${did}/sensitivity`,
  PATIENT_DOCUMENT:             (pid: string, did: string) => `/patients/${pid}/documents/${did}`,
  PATIENT_DOCUMENT_ZIP:         (pid: string) => `/patients/${pid}/documents/zip`,
} as const;

// ─── Legal / compliance ───────────────────────────────────────────────────────
// Bump both when TOS or Privacy Policy is materially updated.
// These are sent to the backend so the audit log records the exact version the
// user saw at consent time.  Must stay in sync with POLICY_VERSION in
// kaagazy-backend/app/core/constants.py.

export const TOS_VERSION = "2026-06-29";
export const PP_VERSION  = "2026-06-29";

// ─── Dev seed identifiers ─────────────────────────────────────────────────────
// Match the backend MOCK_USER_ID (AUTH_ENABLED=False) and scripts/seed_dev.py.
// Used as fallbacks when URL params are absent in development.

export const DEV_PATIENT_ID = "44444444-4444-4444-4444-444444444444";
export const DEV_DOCTOR_ID  = "11111111-1111-1111-1111-111111111111";

// ─── Offline sync ─────────────────────────────────────────────────────────────
// Dexie database schema lives in src/db/kaagazy.db.ts.

export const DEVICE_ID_KEY = "swasthiq_device_id";

/** Max flush attempts before a sync_queue item is marked "failed". */
export const SYNC_MAX_RETRIES = 10;

/** Timeout for POST /visits/sync — prevents stuck "syncing" on dead network. */
export const SYNC_FETCH_TIMEOUT_MS = 30_000;

/** Age after which completed sync_queue rows are pruned from IndexedDB. */
export const SYNC_DONE_RETENTION_MS = 24 * 60 * 60 * 1000;

/** Background push-engine cycle interval — fires when navigator.onLine. */
export const SYNC_PUSH_INTERVAL_MS = 30_000;

/**
 * Mandated push order. Records are pushed entity-by-entity in this exact
 * sequence so server-side foreign keys never reject a child before its
 * parent exists. Within each entity, ascending client_timestamp.
 */
// "doctor" is intentionally absent. Doctors are Users (role=Doctor) created
// during clinic onboarding — an admin operation, never an offline-sync operation.
// There is no server Doctor table; the User table is owned by auth/onboarding.
export const SYNC_PUSH_ORDER = [
  "patient",
  "allergy",
  "visit",
  "vitals",
  "appointment",
  "prescription",
] as const;
// ─── Pull engine (server → client) ───────────────────────────────────────────

/** localStorage key for the last successful pull's server timestamp. */
export const PULL_LAST_SYNC_KEY = "kaagazy_last_sync_timestamp";

/** Periodic pull interval (5 minutes) while the app is open and online. */
export const PULL_INTERVAL_MS = 5 * 60 * 1000;

/** Network-error retry attempts for one page (2s, 4s, 8s backoff). */
export const PULL_MAX_RETRIES = 3;

/** Server-side page size cap. Sent as ?limit=… on every pull request. */
export const PULL_PAGE_LIMIT = 100;

/**
 * Rows per entity per bootstrap page — the server's MAX_PAGE_LIMIT. Every page
 * is a separate authenticated request that re-runs the whole auth chain, so a
 * year-old clinic at 100 rows took 20-40 sequential pages on every clinic
 * switch. The server cuts a page short by bytes (BOOTSTRAP_PAGE_BYTE_BUDGET)
 * so long SOAP notes still fit Lambda's 6 MB response cap.
 *
 * Only used once a page has come back with `page_byte_budget`. A backend
 * without the budget accepts limit=500 too, and 500 long notes can overrun the
 * 6 MB cap into a 502 — and the frontend deploys ahead of the backend by
 * default. Until the server says it cuts by bytes, pages stay at
 * BOOTSTRAP_SAFE_PAGE_LIMIT.
 */
export const BOOTSTRAP_PAGE_LIMIT = 500;

/** Bootstrap page size before the server has advertised a byte budget. */
export const BOOTSTRAP_SAFE_PAGE_LIMIT = PULL_PAGE_LIMIT;

/** Endpoint for the server-to-client pull. */
export const PULL_ENDPOINT = "/sync/pull";

/** localStorage key for the wall-clock time of the last successful pull. */
export const PULL_LAST_SYNCED_AT_KEY = "kaagazy_last_synced_at";

// ─── Bootstrap engine (initial server → client sync) ─────────────────────────

/** Endpoint for the one-shot initial sync. Called before /sync/pull on a fresh device. */
export const BOOTSTRAP_ENDPOINT = "/sync/bootstrap";

/**
 * localStorage key set when /sync/bootstrap has completed at least once.
 * The pull loop refuses to run until this key is present, so a half-bootstrapped
 * cache never serves as the basis for a delta sync.
 */
export const BOOTSTRAP_COMPLETED_AT_KEY = "kaagazy_bootstrap_completed_at";

/**
 * DPDPA Article 5 purpose declaration sent with every /sync/bootstrap request.
 * Server-side allowlist lives in `sync/services.py::ALLOWED_BOOTSTRAP_PURPOSES`
 * — keep these strings in sync. The value is persisted in the compliance audit
 * trail and used in DPDPA Article 12 disclosure reports.
 */
export const BOOTSTRAP_PURPOSE_DEVICE_PROVISIONING = "device_provisioning";

// ─── Security lifecycle ───────────────────────────────────────────────────────
// Time without user activity (mousedown / keydown / touchstart) before the
// in-memory DEK is wiped. The on-disk encrypted IndexedDB is untouched —
// the next interaction transparently re-fetches the wrapped DEK from the
// server while the Amplify session is still valid.
export const IDLE_DEK_WIPE_MS = 15 * 60 * 1000;

export interface VitalRange {
  unit: string;
  rows: { label: string; color: string; value: string }[];
}

export const VITAL_RANGES: Record<string, VitalRange> = {
  "Heart Rate":     { unit: "bpm",   rows: [{ label: "Low", color: "text-amber-400", value: "< 60" }, { label: "Normal", color: "text-emerald-400", value: "60 – 100" }, { label: "High", color: "text-red-400", value: "> 100" }] },
  "Temperature":    { unit: "°C",    rows: [{ label: "Low", color: "text-amber-400", value: "< 36" }, { label: "Normal", color: "text-emerald-400", value: "36 – 37.5" }, { label: "High", color: "text-red-400", value: "> 37.5" }] },
  "Blood Pressure": { unit: "mmHg",  rows: [{ label: "Low", color: "text-amber-400", value: "< 90/60" }, { label: "Normal", color: "text-emerald-400", value: "90/60 – 140/90" }, { label: "High", color: "text-red-400", value: "> 140/90" }] },
  "SpO2":           { unit: "%",     rows: [{ label: "Low", color: "text-amber-400", value: "< 94" }, { label: "Normal", color: "text-emerald-400", value: "≥ 94" }] },
  "Respiration":    { unit: "/min",  rows: [{ label: "Low", color: "text-amber-400", value: "< 12" }, { label: "Normal", color: "text-emerald-400", value: "12 – 20" }, { label: "High", color: "text-red-400", value: "> 20" }] },
  "BMI":            { unit: "kg/m²", rows: [{ label: "Under", color: "text-amber-400", value: "< 18.5" }, { label: "Normal", color: "text-emerald-400", value: "18.5 – 30" }, { label: "Over", color: "text-red-400", value: "> 30" }] },
  "Blood Sugar":    { unit: "mg/dL", rows: [{ label: "Low", color: "text-amber-400", value: "< 70" }, { label: "Normal", color: "text-emerald-400", value: "70 – 140" }, { label: "High", color: "text-red-400", value: "> 140" }] },
};

// ─── Billing / subscription ───────────────────────────────────────────────────
// All prices are INR, exclusive of GST. Mirror of backend app/core/constants.py.

// Trial
export const TRIAL_DURATION_DAYS = 14;
export const TRIAL_DURATION_DISPLAY = "14 days";
export const TRIAL_PRICE_DISPLAY = "₹1";
export const TRIAL_AMOUNT_PAISE = 100;  // ₹1

// Plan prices (INR ex-GST) — pilot: Base annual only @ ₹14,499
// Monthly/Premium kept for legacy subscription banners only.
export const PLAN_BASE_PRICE_INR = 1499;        // legacy monthly (not sellable)
export const PLAN_PREMIUM_PRICE_INR = 2199;     // legacy premium (not sellable)
export const PLAN_ANNUAL_MONTHS = 12;

// Base annual is a fixed pilot price (not monthly × 12).
export const PLAN_BASE_ANNUAL_PRICE_INR = 14499;
export const PLAN_PREMIUM_ANNUAL_PRICE_INR = PLAN_PREMIUM_PRICE_INR * PLAN_ANNUAL_MONTHS; // legacy
export const PLAN_BASE_ANNUAL_PRICE_PAISE = PLAN_BASE_ANNUAL_PRICE_INR * 100;
export const PLAN_PREMIUM_ANNUAL_PRICE_PAISE = PLAN_PREMIUM_ANNUAL_PRICE_INR * 100;

// Per-use credit costs (INR ex-GST)
export const AI_SCRIBE_CREDIT_COST_INR = 3.0;
export const COMMS_CREDIT_COST_INR = 0.5;

// Custom credit top-up — multiples of 100 starting from 100
export const CUSTOM_CREDITS_MIN = 100;
export const CUSTOM_CREDITS_STEP = 100;

export const ONBOARDING_STATUS_ROUTES: Record<string, string> = {
  clinic_details_pending:     "/onboarding/clinic-details",
  documents_pending:          "/onboarding/documents",
  email_verification_pending: "/onboarding/select-plan",
};
